import { useEffect, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExportsBriefing from "@/components/ExportsBriefing";
import { Anchor, LogOut, TrendingDown, TrendingUp, Minus, ArrowUpRight, ArrowDownRight, Ship, Clock, ShieldCheck, Download } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, BarChart, Bar, Cell, ReferenceLine,
} from "recharts";

const ACCENT = { amber: "#F59E0B", blue: "#3B82F6", green: "#10B981", purple: "#8B5CF6", red: "#EF4444" };
const teuFmt = (v) => `${Math.round(v / 1000)}k TEU`;
const dayFmt = (v) => `$${Number(v).toLocaleString()}/day`;
const bblFmt = (v) => `$${v}/bbl`;
const pctFmt = (v) => `${v > 0 ? "+" : ""}${v}%`;
const pctRelFmt = (v) => `${v}%`;

const SimpleTooltip = ({ active, payload, label, fmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-border bg-slate-50 px-3 py-2 font-mono-plex text-xs">
      <div className="text-muted-foreground mb-1 tracking-widest uppercase text-[10px]">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="text-foreground ml-auto">{fmt ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const date = payload[0]?.payload?.date;
  return (
    <div className="border border-border bg-slate-50 px-3 py-2 font-mono-plex text-xs">
      <div className="text-muted-foreground mb-1 tracking-widest uppercase text-[10px]">{date ? `${label} · ${date}` : label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2" style={{ background: p.color }} />
          <span className="capitalize text-muted-foreground">{p.name}</span>
          <span className="text-foreground ml-auto">{typeof p.value === "number" && p.value > 200 ? `$${p.value}` : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const DeltaBadge = ({ delta, suffix = "%" }) => {
  const positive = delta >= 0;
  return (
    <span className={`inline-flex items-center gap-1 font-mono-plex text-xs ${positive ? "text-emerald-400" : "text-red-400"}`}>
      {positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {positive ? "+" : ""}{delta}{suffix}
    </span>
  );
};

const trendIcon = { down: <TrendingDown className="h-3.5 w-3.5 text-emerald-400" />, up: <TrendingUp className="h-3.5 w-3.5 text-red-400" />, flat: <Minus className="h-3.5 w-3.5 text-muted-foreground" /> };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [view, setView] = useState("imports");
  const [bunkerUnit, setBunkerUnit] = useState("bbl");
  const [bunkerPorts, setBunkerPorts] = useState({ rotterdam: true, singapore: true, houston: true });

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/market/briefing/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "PACT-Group-Market-Briefing.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Briefing downloaded", { description: "PACT-Group-Market-Briefing.pdf" });
    } catch (e) {
      toast.error("Download failed", { description: "Could not generate the PDF. Please try again." });
    } finally {
      setDownloading(false);
    }
  };

  const loadBriefing = () => {
    setLoading(true);
    setError(false);
    api
      .get("/market/briefing")
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBriefing();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="font-mono-plex text-xs tracking-widest text-muted-foreground uppercase animate-pulse">Loading briefing…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-testid="briefing-error">
        <p className="text-sm text-muted-foreground">We couldn't load the briefing. Please try again.</p>
        <button
          onClick={loadBriefing}
          data-testid="briefing-retry-button"
          className="border border-border px-4 py-2 text-xs uppercase tracking-widest text-foreground hover:border-muted-foreground/40 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const { meta, kpis, rate_trend, capacity_trend, trade_lanes, commentary, briefing_notes, capacity_moves, capacity_reliability, lane_vs_world, yoy_capacity, charter_rates, reliability_detail, global_drivers, carrier_operations } = data;

  const bunkerConv = (v) => (v == null ? null : bunkerUnit === "bbl" ? Math.round(v / (global_drivers?.bunker?.bbl_per_tonne || 6.35)) : v);
  const bunkerData = (global_drivers?.bunker?.series || []).map((d) => ({
    month: d.month, yr: d.yr,
    rotterdam: bunkerConv(d.rotterdam),
    singapore: bunkerConv(d.singapore),
    houston: bunkerConv(d.houston),
    sg_fwd: bunkerConv(d.sg_fwd),
  }));
  const bunkerYMax = bunkerUnit === "bbl" ? 160 : 1000;
  const bunkerUnitLabel = bunkerUnit === "bbl" ? "US$/barrel" : "US$/tonne";
  const bunkerAxisFmt = (v) => (bunkerUnit === "bbl" ? `$${v}` : `$${v}`);
  const togglePort = (p) => setBunkerPorts((s) => ({ ...s, [p]: !s[p] }));

  const BunkerXTick = ({ x, y, payload }) => {
    const row = (global_drivers?.bunker?.series || [])[payload.index] || {};
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={12} textAnchor="middle" fontSize={10} fill="#64748B">{payload.value}</text>
        {row.yr ? <text x={0} y={0} dy={26} textAnchor="middle" fontSize={11} fontWeight={600} fill="#334155">{row.yr}</text> : null}
      </g>
    );
  };

  const downloadBunkerCsv = () => {
    const rows = [["Month", "Year", "Rotterdam (US$/tonne)", "Singapore (US$/tonne)", "Houston (US$/tonne)", "Singapore forward (US$/tonne)"]];
    (global_drivers?.bunker?.series || []).forEach((d) => {
      rows.push([d.month, d.yr || "", d.rotterdam ?? "", d.singapore ?? "", d.houston ?? "", d.sg_fwd ?? ""]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "PACT-bunker-fuel-by-port.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("CSV downloaded", { description: "PACT-bunker-fuel-by-port.csv" });
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-[1400px] px-6 py-10" data-testid="market-insights">
        {/* Title block */}
        <div className="mb-10 animate-fade-up flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div>
            <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
              {meta.period} · {meta.prepared} · {meta.access_note}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">{view === "exports" ? "Australia-World Trade Briefing" : meta.title}</h1>
            <p className="text-muted-foreground max-w-2xl leading-relaxed">{meta.subtitle}</p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger data-testid="briefing-view-select" className="w-[150px] h-[46px] bg-white border border-border rounded-md font-mono-plex text-xs tracking-widest uppercase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="imports" data-testid="briefing-view-imports">Imports</SelectItem>
                <SelectItem value="exports" data-testid="briefing-view-exports">Exports</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={downloadPdf}
              disabled={downloading || view === "exports"}
              data-testid="download-briefing-pdf-button"
              className="group inline-flex items-center gap-2 bg-[#0072B8] px-4 py-3 text-xs font-mono-plex tracking-widest uppercase text-white rounded-md hover:bg-[#005A93] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              {downloading ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </div>

        {view === "exports" ? (
          <ExportsBriefing />
        ) : (
          <>

        {/* Meta strip */}
        <section className="border-t border-l border-border grid grid-cols-2 lg:grid-cols-4 mb-12" data-testid="meta-strip">
          {[
            ["Reporting window", meta.reporting_window],
            ["Lane focus", meta.lane_focus],
            ["Benchmark", meta.benchmark],
            ["Prepared", meta.prepared_date],
          ].map(([k, v], i) => (
            <div key={i} className="border-r border-b border-border bg-white p-5">
              <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-2">{k}</div>
              <div className="text-sm font-medium">{v}</div>
            </div>
          ))}
        </section>

        {/* KPIs */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-12" data-testid="kpi-grid">
          {kpis.map((k, i) => (
            <div key={i} className="border-r border-b border-border bg-white p-6" data-testid={`kpi-card-${i}`}>
              <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">{k.label}</div>
              <div className="text-[11px] text-muted-foreground/70 mb-4">{k.sublabel}</div>
              <div className="font-display text-3xl tracking-tight mb-2">{k.value}</div>
              <div className="flex items-center gap-2">
                <DeltaBadge delta={k.delta} suffix={k.delta_suffix} />
                <span className="text-[11px] text-muted-foreground">{k.unit}</span>
              </div>
            </div>
          ))}
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2 border border-border bg-white p-6" data-testid="rate-trend-chart">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Spot Rate Trend</h2>
                <p className="text-xs text-muted-foreground mt-1">SHA-SYD (Shanghai → Sydney, 40ft, US$) vs Xeneta LCH-SYD (Laem Chabang → Sydney) · weekly</p>
              </div>
              <div className="hidden sm:flex items-center gap-4 font-mono-plex text-[11px]">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#3B82F6]" />SHA-SYD</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#059669]" />LCH-SYD</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={rate_trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                <XAxis dataKey="wk" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} interval={5} minTickGap={16} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={["dataMin - 200", "dataMax + 200"]} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" name="SHA-SYD" dataKey="sha_syd" stroke="#3B82F6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls />
                <Line type="monotone" name="LCH-SYD" dataKey="xeneta_lch_syd" stroke="#059669" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-border bg-white p-6" data-testid="capacity-trend-chart">
            <h2 className="text-lg font-semibold tracking-tight">Capacity Index</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-6">Deployed TEU on lane (base 100)</p>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={capacity_trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="capFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={["dataMin - 4", "dataMax + 4"]} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="index" stroke="#3B82F6" strokeWidth={2} fill="url(#capFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Trade lanes */}
        <section className="mb-12" data-testid="trade-lanes">
          <h2 className="text-lg font-semibold tracking-tight mb-1">Market Spot Rate · Asia → Australia</h2>
          <p className="text-xs text-muted-foreground mb-6">Indicative FAK spot pricing (40HC), transit &amp; schedule reliability by lane · Cheat Sheet, 1 Sep 2026</p>
          <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {trade_lanes.map((l, i) => (
              <div key={i} className="border-r border-b border-border bg-white p-6" data-testid={`trade-lane-${i}`}>
                <div className="flex items-start justify-between mb-4">
                  <Ship className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  {trendIcon[l.trend]}
                </div>
                <div className="font-mono-plex text-[11px] tracking-wide text-muted-foreground mb-1">{l.origin}</div>
                <div className="text-sm font-medium mb-4">→ {l.destination}</div>
                <div className="font-display text-2xl tracking-tight mb-4">{l.rate}</div>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Transit</span>
                    <span className="text-foreground font-mono-plex">{l.transit}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Reliability</span>
                    <span className="text-foreground font-mono-plex">{l.reliability}%</span>
                  </div>
                  <div className="h-1 w-full bg-secondary mt-1">
                    <div className="h-full bg-emerald-500" style={{ width: `${l.reliability}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Carrier capacity moves */}
        {capacity_moves && (
          <section className="mb-12" data-testid="capacity-moves">
            <h2 className="text-lg font-semibold tracking-tight mb-1">Carrier capacity moves that touch your lane</h2>
            <p className="text-xs text-muted-foreground mb-6">{capacity_moves.source}</p>
            <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {capacity_moves.cards.map((c, i) => (
                <div key={i} className="border-r border-b border-border bg-white p-6 relative" data-testid={`capacity-move-${i}`}>
                  <div className="absolute left-0 top-0 h-full w-1" style={{ background: ACCENT[c.accent] }} />
                  <div className="font-mono-plex text-[10px] tracking-widest uppercase mb-4" style={{ color: ACCENT[c.accent] }}>{c.tag}</div>
                  <div className="font-display text-2xl tracking-tight mb-3">{c.value}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Capacity & Carrier Reliability */}
        {capacity_reliability && (
          <section className="mb-12" data-testid="capacity-reliability">
            <div className="font-mono-plex text-[11px] tracking-[0.2em] uppercase text-[#F59E0B] mb-3">Market insight on services</div>
            <h2 className="font-display text-3xl tracking-tight mb-4">Capacity &amp; Carrier Reliability</h2>
            <p className="text-muted-foreground max-w-4xl leading-relaxed mb-8">{capacity_reliability.intro}</p>
            <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3">
              {capacity_reliability.stats.map((s, i) => (
                <div key={i} className="border-r border-b border-border bg-white p-6 relative" data-testid={`cap-rel-stat-${i}`}>
                  <div className="absolute left-0 top-0 h-full w-1" style={{ background: ACCENT[s.accent] }} />
                  <div className="font-display text-3xl tracking-tight mb-1">{s.value}</div>
                  <div className="text-sm font-semibold mb-3">{s.label}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/60 mt-4 font-mono-plex tracking-wide">{capacity_reliability.source}</p>
          </section>
        )}

        {/* Australia Lane vs Rest of World */}
        {lane_vs_world && (
          <section className="mb-12" data-testid="lane-vs-world">
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-mono-plex text-sm text-[#F59E0B]">02</span>
              <h2 className="font-display text-3xl tracking-tight">Australia Lane vs. Rest of World</h2>
            </div>
            <p className="text-muted-foreground max-w-4xl leading-relaxed mb-8">{lane_vs_world.intro}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight">Far East → Australasia/Oceania</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-6">Monthly liftings (TEU)</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={lane_vs_world.far_east} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                    <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip content={<SimpleTooltip fmt={teuFmt} />} cursor={{ fill: "#64748B", fillOpacity: 0.08 }} />
                    <Bar dataKey="teu" name="Far East" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight">Europe → Australasia/Oceania</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-6">Monthly liftings (TEU) — note the smaller scale</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={lane_vs_world.europe} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                    <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip content={<SimpleTooltip fmt={teuFmt} />} cursor={{ fill: "#64748B", fillOpacity: 0.08 }} />
                    <Bar dataKey="teu" name="Europe" fill="#71717A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="border border-border bg-white/50 p-4 mt-6">
              <p className="text-xs text-muted-foreground leading-relaxed">{lane_vs_world.note}</p>
            </div>
            <p className="text-[11px] text-muted-foreground/60 mt-3 font-mono-plex tracking-wide">{lane_vs_world.source}</p>
          </section>
        )}

        {/* YoY capacity growth by trade */}
        {yoy_capacity && (
          <section className="mb-12" data-testid="yoy-capacity">
            <div className="border border-border bg-white p-6">
              <h3 className="text-base font-semibold tracking-tight">Year-on-year capacity growth by trade</h3>
              <p className="text-xs text-muted-foreground mt-1 mb-6">Cellular capacity deployed — Australasia highlighted</p>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={yoy_capacity.data} layout="vertical" margin={{ top: 5, right: 40, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} horizontal={false} />
                  <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} tickFormatter={(v) => `${v}%`} domain={[-5, 15]} />
                  <YAxis type="category" dataKey="trade" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} width={140} />
                  <Tooltip content={<SimpleTooltip fmt={pctFmt} />} cursor={{ fill: "#64748B", fillOpacity: 0.08 }} />
                  <ReferenceLine x={0} stroke="#3f3f46" />
                  <Bar dataKey="pct" name="YoY">
                    {yoy_capacity.data.map((d, i) => (
                      <Cell key={i} fill={d.highlight ? "#F59E0B" : d.pct < 0 ? "#EF4444" : "#3B82F6"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-muted-foreground/60 mt-3 font-mono-plex tracking-wide">Source · {yoy_capacity.source}</p>
            </div>
            <div className="border border-border bg-white/50 p-4 mt-4">
              <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground font-semibold">Read:</span> {yoy_capacity.read}</p>
            </div>
          </section>
        )}

        {/* Vessel charter rates */}
        {charter_rates && (
          <section className="mb-12" data-testid="charter-rates">
            <div className="border border-border bg-white p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="text-base font-semibold tracking-tight">Vessel charter rates (US$/day), Jan → Sep 2026</h3>
                  <p className="text-xs text-muted-foreground mt-1">12-month time-charter hire by vessel size — monthly</p>
                </div>
                <div className="hidden sm:flex items-center gap-4 font-mono-plex text-[11px]">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#3B82F6]" />≈4,250 TEU</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#F59E0B]" />≈1,700 TEU (feeder)</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={charter_rates.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} domain={[27000, 41000]} />
                  <Tooltip content={<SimpleTooltip fmt={dayFmt} />} />
                  <Line type="monotone" name="≈4,250 TEU" dataKey="large" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" name="≈1,700 TEU" dataKey="feeder" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-muted-foreground/60 mt-3 font-mono-plex tracking-wide">Source · {charter_rates.source}</p>
            </div>
          </section>
        )}

        {/* Schedule Reliability & Capacity Tightness */}
        {reliability_detail && (
          <section className="mb-12" data-testid="reliability-detail">
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-mono-plex text-sm text-[#F59E0B]">06</span>
              <h2 className="font-display text-3xl tracking-tight">Schedule Reliability &amp; Capacity Tightness</h2>
            </div>
            <p className="text-muted-foreground max-w-4xl leading-relaxed mb-8">{reliability_detail.intro}</p>
            <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-6">
              {reliability_detail.stats.map((s, i) => (
                <div key={i} className="border-r border-b border-border bg-white p-6" data-testid={`rel-stat-${i}`}>
                  <div className="font-display text-3xl tracking-tight mb-2">{s.value}</div>
                  <div className={`font-mono-plex text-xs mb-3 ${s.dir === "up" ? "text-emerald-400" : "text-red-400"}`}>{s.dir === "up" ? "▲" : "▼"} {s.delta}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{s.body}</p>
                  <p className="text-[10px] text-muted-foreground/50 font-mono-plex tracking-widest uppercase">{s.source}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight">Asia–Oceania reliability trend</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-4">Last 5 rolling 2-month periods (%)</p>
                <div className="hidden sm:flex items-center gap-4 font-mono-plex text-[11px] mb-2">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#3B82F6]" />Asia–Oceania</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#F59E0B]" />Global avg</span>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={reliability_detail.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                    <XAxis dataKey="period" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                    <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[50, 70]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<SimpleTooltip fmt={pctRelFmt} />} />
                    <ReferenceLine y={reliability_detail.global_avg} stroke="#3f3f46" strokeDasharray="4 4" />
                    <Line type="monotone" name="Asia–Oceania" dataKey="asia_oceania" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" name="Global" dataKey="global" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight">Carrier reliability, Asia ↔ Oceania</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-4">{reliability_detail.carriers_note}</p>
                <div className="hidden sm:flex items-center gap-4 font-mono-plex text-[11px] mb-2">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#3B82F6]" />Headhaul</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#F59E0B]" />Backhaul</span>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={reliability_detail.carriers} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                    <XAxis dataKey="carrier" stroke="#64748B" fontSize={9} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} interval={0} angle={-30} textAnchor="end" height={60} />
                    <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<SimpleTooltip fmt={pctRelFmt} />} cursor={{ fill: "#64748B", fillOpacity: 0.08 }} />
                    <Bar name="Headhaul" dataKey="headhaul" fill="#3B82F6" />
                    <Bar name="Backhaul" dataKey="backhaul" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        )}

        {/* Global drivers */}
        {global_drivers && (
          <section className="mb-12" data-testid="global-drivers">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-mono-plex text-sm text-[#F59E0B]">08</span>
              <h2 className="font-display text-3xl tracking-tight">Global Drivers That Reach Your Supply Chain</h2>
            </div>
            <p className="text-muted-foreground mb-8">{global_drivers.subtitle}</p>

            <div className="border border-border bg-white p-6 mb-6" data-testid="bunker-chart-card">
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="text-base font-semibold tracking-tight">{global_drivers.bunker.title || "Bunker fuel prices by port"}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{global_drivers.bunker.note}</p>
                </div>
                <button
                  onClick={downloadBunkerCsv}
                  data-testid="bunker-csv-button"
                  className="inline-flex items-center gap-2 border border-border rounded-md px-3 py-1.5 text-[11px] font-mono-plex tracking-widest uppercase text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  <Download className="h-3.5 w-3.5" /> CSV
                </button>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-5">
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  {[["bbl", "US$ / barrel"], ["tonne", "US$ / tonne"]].map(([u, lbl]) => (
                    <button
                      key={u}
                      onClick={() => setBunkerUnit(u)}
                      data-testid={`bunker-unit-${u}`}
                      className={`px-3 py-1.5 text-[11px] font-mono-plex tracking-wide transition-colors ${bunkerUnit === u ? "bg-[#0072B8] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap ml-1">
                  {[["rotterdam", "Rotterdam (Europe)", "#1E293B"], ["singapore", "Singapore (Asia hub)", "#F59E0B"], ["houston", "Houston (US Gulf)", "#14B8A6"]].map(([key, lbl, color]) => (
                    <button
                      key={key}
                      onClick={() => togglePort(key)}
                      data-testid={`bunker-port-${key}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-mono-plex transition-colors ${bunkerPorts[key] ? "border-border text-foreground" : "border-border/60 text-muted-foreground/50"}`}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: bunkerPorts[key] ? color : "#CBD5E1" }} />
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={bunkerData} margin={{ top: 5, right: 10, left: 0, bottom: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} interval={0} tick={<BunkerXTick />} height={40} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[0, bunkerYMax]} tickFormatter={bunkerAxisFmt} label={{ value: bunkerUnitLabel, angle: -90, position: "insideLeft", fontSize: 10, fill: "#94A3B8", style: { textAnchor: "middle" } }} />
                  <Tooltip content={<SimpleTooltip fmt={bunkerAxisFmt} />} />
                  {bunkerPorts.rotterdam && <Line type="monotone" name="Rotterdam" dataKey="rotterdam" stroke="#1E293B" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />}
                  {bunkerPorts.singapore && <Line type="monotone" name="Singapore" dataKey="singapore" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />}
                  {bunkerPorts.houston && <Line type="monotone" name="Houston" dataKey="houston" stroke="#14B8A6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />}
                  {bunkerPorts.singapore && <Line type="monotone" name="Singapore forward" dataKey="sg_fwd" stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 5" dot={false} activeDot={{ r: 4 }} connectNulls />}
                </LineChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-muted-foreground/60 mt-3 font-mono-plex tracking-wide">Source · {global_drivers.bunker.source}</p>
            </div>

            <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-3 mb-4">
              {global_drivers.bunker.stats.map((s, i) => (
                <div key={i} className="border-r border-b border-border bg-white p-6" data-testid={`bunker-stat-${i}`}>
                  <div className="font-display text-2xl tracking-tight mb-1">{s.value}</div>
                  <div className="font-mono-plex text-[10px] tracking-widest uppercase text-emerald-600 mb-3">{s.label}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>

            {global_drivers.bunker.footnote && (
              <div className="border border-border bg-white/50 p-4 mb-6" data-testid="bunker-footnote">
                <p className="text-xs text-muted-foreground leading-relaxed">{global_drivers.bunker.footnote}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight mb-5">Three structural factors to watch</h3>
                <div className="space-y-4">
                  {global_drivers.structural_factors.map((f, i) => (
                    <div key={i} className="flex gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
                      <div className="w-24 shrink-0 text-sm font-semibold">{f.factor}</div>
                      <div className="flex-1 text-sm text-muted-foreground leading-relaxed">{f.status}</div>
                      <div className="shrink-0"><span className="font-mono-plex text-[10px] uppercase tracking-widest border border-border px-2 py-1 text-muted-foreground whitespace-nowrap">{f.risk}</span></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-border bg-white p-6">
                <h3 className="text-base font-semibold tracking-tight mb-5">Suez Canal return — scenario probabilities</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="text-left font-normal pb-3">Scenario</th>
                      <th className="text-left font-normal pb-3">Rate impact</th>
                      <th className="text-right font-normal pb-3">Prob.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {global_drivers.suez_scenarios.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-3 pr-3 font-medium">{r.scenario}</td>
                        <td className="py-3 pr-3 text-muted-foreground">{r.impact}</td>
                        <td className="py-3 text-right"><span className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">{r.prob}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-muted-foreground/50 font-mono-plex tracking-widest uppercase mt-4">{global_drivers.suez_scenarios.source}</p>
              </div>
            </div>
          </section>
        )}

        {/* Commentary */}
        <section data-testid="market-commentary">
          <h2 className="text-lg font-semibold tracking-tight mb-1">Market Commentary</h2>
          <p className="text-xs text-muted-foreground mb-6">Analyst notes for the briefing period</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {commentary.map((c, i) => (
              <article key={i} className="border border-border bg-white p-6 hover:border-muted-foreground/30 transition-colors" data-testid={`commentary-${i}`}>
                <span className="inline-block font-mono-plex text-[10px] tracking-widest uppercase text-[#3B82F6] border border-[#3B82F6]/30 px-2 py-0.5 mb-4">
                  {c.tag}
                </span>
                <h3 className="font-display text-xl tracking-tight mb-3 leading-snug">{c.headline}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Carrier Operations & September Outlook (section 10) */}
        {carrier_operations && (
          <section className="mt-12" data-testid="carrier-operations">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-mono-plex text-sm text-[#F59E0B]">10</span>
              <h2 className="font-display text-3xl tracking-tight">Carrier Operations &amp; September Outlook</h2>
            </div>
            <p className="text-muted-foreground max-w-4xl leading-relaxed mb-8">{carrier_operations.subtitle}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {carrier_operations.cards.map((c, i) => (
                <article key={i} className="border border-border bg-white p-6" data-testid={`carrier-op-${i}`}>
                  <div className="font-mono-plex text-[11px] tracking-[0.2em] uppercase mb-5" style={{ color: ACCENT[c.accent] || ACCENT.amber }}>{c.tag}</div>
                  <div className="space-y-4">
                    {c.items.map((it, j) => (
                      <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                        <span className="text-foreground font-semibold">{it.label}:</span> {it.body}
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
        </>
        )}

        <footer className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground">PACT Group · Market Briefing</span>
          <span className="text-[11px] text-muted-foreground/60">Sample data shown for demonstration purposes.</span>
        </footer>
      </main>
    </div>
  );
}
